import React, { useContext, useEffect, useRef, useState } from "react";
import InputMaskField from "react-input-mask";
import { RecadastramentoContext } from "@contexts";
import { Alert, Button, Col, ComboBox, Form, Input, Label, Row } from "@intech/web-ui";
import { UFEntidade, WebRecadParticipanteDadosEntidade } from "@entidades";
import { RecadastramentoService } from "@services";
import { StringIsNullOrEmpty, getCEP } from "@helpers";
import { LoadingComponent } from "@components";
import { FaAngleDoubleLeft, FaAngleDoubleRight } from "react-icons/fa";

interface IProps {
  avancar: (PassoEspecifico?: number | null) => Promise<void>;
  voltar: (PassoEspecifico?: number | null) => Promise<void>;
}

export const Passo4: React.FC<IProps> = ({
  avancar,
  voltar
}) => {
  const context = useContext(RecadastramentoContext);
  const isMountedRef = useRef(true);
  const form = useRef<Form>(null);

  const [Cep, setCep] = useState<string>("");
  const [Endereco, setEndereco] = useState<string>("");
  const [Numero, setNumero] = useState<string>("");
  const [Complemento, setComplemento] = useState<string>("");
  const [Bairro, setBairro] = useState<string>("");
  const [Cidade, setCidade] = useState<string>("");
  const [Estado, setEstado] = useState<string>("");
  const [Email, setEmail] = useState<string>("");
  const [TelefoneResidencial, setTelefoneResidencial] = useState<string>("");
  const [TelefoneCelular, setTelefoneCelular] = useState<string>("");
  const [ListaUF, setListaUF] = useState<Array<UFEntidade>>([]);

  const [Loading, setLoading] = useState<boolean>(true);
  const [Erro, setErro] = useState<string>("");
  const [ErroForm, setErroForm] = useState<string>("");

  useEffect(() => () => { isMountedRef.current = false }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (!isMountedRef.current)
          return;

        const dados = await context.buscarDadosParticipante();
        const dadosOriginais = await context.buscarDadosOriginais();

        const ListaUF = await RecadastramentoService.BuscarListaUF();

        setCep(dados.CEP_ENTID || dadosOriginais.CEP_ENTID || "");
        setEndereco(dados.END_ENTID || dadosOriginais.END_ENTID || "");
        setNumero(dados.NR_END_ENTID || dadosOriginais.NR_END_ENTID || "");
        setComplemento(dados.COMP_END_ENTID || dadosOriginais.COMP_END_ENTID || "");
        setBairro(dados.BAIRRO_ENTID || dadosOriginais.BAIRRO_ENTID || "");
        setCidade(dados.CID_ENTID || dadosOriginais.CID_ENTID || "");
        setEstado(dados.UF_ENTID || dadosOriginais.UF_ENTID || "");
        setEmail(dados.EMAIL_AUX || dadosOriginais.EMAIL_AUX || "");
        setTelefoneResidencial(dados.FONE_ENTID || dadosOriginais.FONE_ENTID || "");
        setTelefoneCelular(dados.FONE_CELULAR || dadosOriginais.FONE_CELULAR || "");
        setListaUF(ListaUF);
      } catch (e) {
        const error: any = e;
        const messsage = error.response ? error.response.data : error;
        setErro(messsage);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function BuscarCep() {
    form?.current?.clear();
    setErroForm("");
    try {
      const cepres = await getCEP(Cep);

      setEndereco(cepres.logradouro || cepres.address);
      setComplemento("");
      setNumero("");
      setBairro(cepres.bairro || cepres.district);
      setCidade(cepres.localidade || cepres.city);
      setEstado(cepres.uf || cepres.state);
    }
    catch (e) {
      setCep("");
      const error: any = e;
      const messsage = error.response ? error.response.data : error;
      setErroForm(messsage);
    }
  };

  async function salvarDados() {
    try {
      if (form?.current?.validate()) {
        if (Email) await RecadastramentoService.ValidarEmail(Email);

        const dadosEstado = ListaUF.filter(x => x.CD_UNID_FED === Estado)[0];

        if (!dadosEstado) {
          setErroForm("Houve um erro ao carregar os dados do seu estado, favor contatar a Fundação.");
          return false;
        }

        const dados = await context.buscarDadosParticipante();
        dados.CEP_ENTID = Cep;
        dados.END_ENTID = Endereco;
        dados.NR_END_ENTID = Numero;
        dados.COMP_END_ENTID = Complemento;
        dados.BAIRRO_ENTID = Bairro;
        dados.CID_ENTID = Cidade;
        dados.UF_ENTID = dadosEstado.CD_UNID_FED;
        dados.DS_UF_ENTID = dadosEstado.DS_UNID_FED;
        dados.EMAIL_AUX = Email;
        dados.FONE_ENTID = TelefoneResidencial;
        dados.FONE_CELULAR = TelefoneCelular;

        context.salvarDadosParticipante(dados);
        return true;
      }
      return false;
    }
    catch (e) {
      const error: any = e;
      const messsage = error.response ? error.response.data : error;
      setErroForm(messsage);
    }
  }

  async function handleVoltar() {
    form?.current?.clear();
    setErroForm("");
    try {
      if (await salvarDados()) {
        voltar();
      }
    }
    catch (e) {
      const error: any = e;
      const messsage = error.response ? error.response.data : error;
      setErroForm(messsage);
    }
  }

  async function handleAvancar() {
    form?.current.clear();
    setErroForm("");
    try {
      if (StringIsNullOrEmpty(Estado)) {
        setErroForm("Campo Estado obrigatório.");
      }

      if (await salvarDados()) {
        avancar();
      }
    }
    catch (e) {
      const error: any = e;
      const messsage = error.response ? error.response.data : error;
      setErroForm(messsage);
    }
  }

  return (
    <>
      {Loading && <LoadingComponent />}

      {!Loading && Erro && <Alert type={'danger'}>{Erro}</Alert>}

      {!Loading && !Erro && (
        <>
          <Form ref={form}>
            <Input
              value={Cep}
              onChange={setCep}
              title={"CEP"}
              mask={"cep"}
              required
              buttonTitle={"Consultar"}
              buttonType={"primary"}
              onButtonClick={BuscarCep}
            />

            <Input
              value={Endereco}
              onChange={setEndereco}
              title={"Endereco"}
              max={60}
              required
            />

            <Input
              value={Numero}
              onChange={setNumero}
              title={"Número"}
              max={6}
              required
            />

            <Input
              value={Complemento}
              onChange={setComplemento}
              title={"Complemento"}
              max={30}
            />

            <Input
              value={Bairro}
              onChange={setBairro}
              title={"Bairro"}
              max={30}
              required
            />

            <Input
              value={Cidade}
              onChange={setCidade}
              title={"Cidade"}
              max={40}
              required
            />

            <ComboBox
              data={ListaUF}
              title={"* Estado"}
              value={Estado}
              onChange={setEstado}
              memberName={"DS_UNID_FED"}
              memberValue={"CD_UNID_FED"}
              emptyText={"Escolha o seu Estado"}
            />

            <Input
              value={Email}
              onChange={setEmail}
              title={"E-mail Pessoal"}
              max={60}
            />

            <div className={"form-group"}>
              <Row>
                <Label
                  value={"Telefone Residencial"}
                  size={2}
                />

                <Col>
                  <div>
                    <InputMaskField
                      className={"form-control"}
                      mask="(99)9999-9999"
                      id={"TelefoneResidencial"}
                      value={TelefoneResidencial}
                      onChange={(e: { target: { value: React.SetStateAction<string>; }; }) => setTelefoneResidencial(e.target.value)}
                    />
                  </div>
                </Col>
              </Row>
            </div>

            <div className={"form-group"}>
              <Row>
                <Label
                  value={"Telefone Celular"}
                  size={2}
                  required
                />

                <Col>
                  <div>
                    <InputMaskField
                      className={"form-control"}
                      mask="(99)99999-9999"
                      id={"TelefoneCelular"}
                      value={TelefoneCelular}
                      onChange={(e: { target: { value: React.SetStateAction<string>; }; }) => setTelefoneCelular(e.target.value)}
                    />
                  </div>
                </Col>
              </Row>
            </div>

            <Alert formAlert type={"danger"} />
            <Alert type={"danger"}>{ErroForm}</Alert>

            <Row>
              <Col>
                <Button
                  type={"secondary"}
                  title={"Voltar"}
                  icon={<FaAngleDoubleLeft />}
                  onClick={handleVoltar}
                  loadingText={"Voltando..."}
                  usesLoading
                  block
                  iconRight
                />
              </Col>

              <Col>
                <Button
                  type={"primary"}
                  title={"Continuar"}
                  icon={<FaAngleDoubleRight />}
                  onClick={handleAvancar}
                  loadingText={"Continuando..."}
                  usesLoading
                  block
                />
              </Col>
            </Row>
          </Form>
        </>)}
    </>
  );
}
